import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Checkbox } from '@rudi/ui/checkbox';
import { Input } from '@rudi/ui/input';
import { Textarea } from '@rudi/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@rudi/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader, QueryState } from '../components/page';
import {
  useEditIdentity,
  useIdentityEntity,
  useMergeIdentity,
  useMoveIdentity,
  useSetOrganisationParent,
} from '../api/hooks';
import type {
  IdentityAlias,
  IdentityDepartment,
  IdentityLevel,
  IdentityListParams,
  IdentityPlaceDetail,
  IdentitySite,
  IdentityTreeSite,
} from '../api/types';
import {
  DataQualityNav,
  EntityLink,
  EntityPickerDialog,
  EntityReview,
  Fact,
  FlagButton,
  LEVEL_NAME,
  LinkEvidence,
  MentionPlace,
  ReasonField,
  Section,
  humanise,
  identityErrorText,
  loadError,
  placeText,
  postalCity,
} from './medical-data-quality';

type PlaceLevel = 'organisation' | 'site' | 'department';

export function OrganisationRoutePage() {
  const { entityId } = useParams({ from: '/data-quality/organisations/$entityId' });
  return <OrganisationDetailPage level="organisation" id={entityId} />;
}

export function CatalogSiteRoutePage() {
  const { entityId } = useParams({ from: '/data-quality/catalog-sites/$entityId' });
  return <OrganisationDetailPage level="site" id={entityId} />;
}

export function DepartmentRoutePage() {
  const { entityId } = useParams({ from: '/data-quality/departments/$entityId' });
  return <OrganisationDetailPage level="department" id={entityId} />;
}

type Dialogs =
  | { kind: 'merge'; level: PlaceLevel; id: string; name: string; filter?: IdentityListParams }
  | { kind: 'move-site'; id: string; name: string; organisationId: string }
  | { kind: 'move-department'; id: string; name: string }
  | { kind: 'parent'; id: string; name: string }
  | { kind: 'edit' }
  | null;

/**
 * An Organisation, Catalog Site or Department: its Organisation's tree of
 * Catalog Sites and Departments, every Mention that linked here and how, and
 * the steward decisions that change them.
 */
export function OrganisationDetailPage({ level, id }: { level: PlaceLevel; id: string }) {
  const entity = useIdentityEntity(level, id);
  const [dialog, setDialog] = useState<Dialogs>(null);
  const merge = useMergeIdentity();
  const move = useMoveIdentity();
  const setParent = useSetOrganisationParent();
  const data = entity.data;
  const detail = data && 'mentions' in data ? (data as IdentityPlaceDetail) : null;
  const redirect = data?.redirect_to ?? null;
  const notFound = (entity.error as { response?: { status?: number } })?.response?.status === 404;
  const name = LEVEL_NAME[level].one;

  return (
    <div>
      <Link
        to="/data-quality/organisations"
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> Organisations
      </Link>
      <DataQualityNav />
      <QueryState isLoading={entity.isLoading} error={notFound ? null : loadError(entity.error)}>
        {notFound ? (
          <p role="alert">
            This {name} was not found. It may have been removed; return to Organisations and search
            again.
          </p>
        ) : redirect ? (
          <div role="alert" className="rounded-3xl border bg-card p-4 text-sm">
            This {name} was merged into another.{' '}
            <EntityLink kind={level} id={redirect}>
              Open the {name} it was merged into
            </EntityLink>
            .
          </div>
        ) : detail ? (
          <>
            <PageHeader
              title={detail.display_name}
              description={headerText(detail)}
              actions={
                <HeaderActions detail={detail} onOpen={setDialog} name={detail.display_name} />
              }
            />
            <Facts detail={detail} />
            <Section title="Organisation tree">
              <PlaceTree detail={detail} onOpen={setDialog} />
            </Section>
            <Section title={`Mentions linked to this ${name} (${detail.mentions.length})`}>
              <MentionsTable detail={detail} />
            </Section>
            <Section title={`Studies (${detail.studies.length})`}>
              {detail.studies.length ? (
                <p className="text-sm">
                  {detail.studies.map(study => study.registry_identifier).join(', ')}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">No active studies.</p>
              )}
            </Section>
            <EntityReview candidates={detail.candidates} findings={detail.findings} />
            {dialog?.kind === 'edit' && (
              <EditNameDialog detail={detail} open onClose={() => setDialog(null)} />
            )}
          </>
        ) : null}
      </QueryState>

      <EntityPickerDialog
        open={dialog?.kind === 'merge'}
        onClose={() => setDialog(null)}
        level={dialog?.kind === 'merge' ? dialog.level : level}
        title={dialog?.kind === 'merge' ? `Merge ${dialog.name}` : ''}
        description={
          dialog?.kind === 'merge'
            ? `Choose the ${LEVEL_NAME[dialog.level].one} that ${dialog.name} really is. Its Mentions move there and its ID redirects.`
            : ''
        }
        confirmLabel="Merge"
        excludeIds={dialog?.kind === 'merge' ? [dialog.id] : []}
        filter={dialog?.kind === 'merge' ? dialog.filter : undefined}
        onConfirm={async (targetId, reason) => {
          if (dialog?.kind !== 'merge' || !targetId) return;
          await merge.mutateAsync({
            level: dialog.level,
            sourceId: dialog.id,
            targetId,
            reason,
          });
          toast.success(`${dialog.name} merged`);
        }}
      />
      <EntityPickerDialog
        open={dialog?.kind === 'move-site'}
        onClose={() => setDialog(null)}
        level="organisation"
        title={dialog?.kind === 'move-site' ? `Move ${dialog.name}` : ''}
        description="Choose the Organisation that runs this Catalog Site. Its Departments move with it."
        confirmLabel="Move"
        excludeIds={dialog?.kind === 'move-site' ? [dialog.organisationId] : []}
        onConfirm={async (targetId, reason) => {
          if (dialog?.kind !== 'move-site' || !targetId) return;
          await move.mutateAsync({ level: 'site', id: dialog.id, parentId: targetId, reason });
          toast.success(`${dialog.name} moved`);
        }}
      />
      <EntityPickerDialog
        open={dialog?.kind === 'move-department'}
        onClose={() => setDialog(null)}
        level="site"
        title={dialog?.kind === 'move-department' ? `Move ${dialog.name}` : ''}
        description="Choose the Catalog Site where this Department works."
        confirmLabel="Move"
        onConfirm={async (targetId, reason) => {
          if (dialog?.kind !== 'move-department' || !targetId) return;
          await move.mutateAsync({
            level: 'department',
            id: dialog.id,
            parentId: targetId,
            reason,
          });
          toast.success(`${dialog.name} moved`);
        }}
      />
      <EntityPickerDialog
        open={dialog?.kind === 'parent'}
        onClose={() => setDialog(null)}
        level="organisation"
        title={dialog?.kind === 'parent' ? `Set the parent of ${dialog.name}` : ''}
        description="A parent groups Organisations, such as hospitals of one chain. It never makes two Investigators one."
        confirmLabel="Set parent"
        excludeIds={dialog?.kind === 'parent' ? [dialog.id] : []}
        allowNone="No parent"
        onConfirm={async (parentId, reason) => {
          if (dialog?.kind !== 'parent') return;
          await setParent.mutateAsync({ id: dialog.id, parentId, reason });
          toast.success(parentId ? 'Parent set' : 'Parent removed');
        }}
      />
    </div>
  );
}

function headerText(detail: IdentityPlaceDetail): string {
  if (detail.kind === 'organisation')
    return `Organisation · ${placeText([detail.city, detail.country]) || 'no city'}`;
  if (detail.kind === 'site')
    return `Catalog Site of ${detail.tree.organisation.display_name} · ${siteAddress(detail)}`;
  const site = detail.tree.sites.find(item => item.id === detail.site_id);
  return `Department at ${site?.display_name ?? 'a Catalog Site'} · ${detail.tree.organisation.display_name}`;
}

function siteAddress(site: IdentitySite): string {
  return (
    placeText([site.street, postalCity(site.postal_code, site.city), site.country]) || 'No address'
  );
}

function pointText(site: IdentitySite): string {
  if (site.latitude === null || site.longitude === null) return 'No point';
  const precision = site.geo_precision ? ` (${humanise(site.geo_precision)})` : '';
  return `${site.latitude.toFixed(4)}, ${site.longitude.toFixed(4)}${precision}`;
}

function HeaderActions({
  detail,
  name,
  onOpen,
}: {
  detail: IdentityPlaceDetail;
  name: string;
  onOpen: (dialog: Dialogs) => void;
}) {
  const renameLabel = detail.kind === 'department' ? 'Rename' : 'Rename and aliases';
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <Button size="sm" variant="outline" onClick={() => onOpen({ kind: 'edit' })}>
        {renameLabel}
      </Button>
      {detail.kind === 'organisation' && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onOpen({ kind: 'parent', id: detail.id, name })}
        >
          Set parent
        </Button>
      )}
      {detail.kind === 'site' && (
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            onOpen({
              kind: 'move-site',
              id: detail.id,
              name,
              organisationId: detail.organisation_id,
            })
          }
        >
          Move
        </Button>
      )}
      {detail.kind === 'department' && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onOpen({ kind: 'move-department', id: detail.id, name })}
        >
          Move
        </Button>
      )}
      <Button
        size="sm"
        variant="outline"
        onClick={() =>
          onOpen({
            kind: 'merge',
            level: detail.kind,
            id: detail.id,
            name,
            filter:
              detail.kind === 'site'
                ? { organisation_id: detail.organisation_id }
                : detail.kind === 'department'
                  ? { site_id: detail.site_id }
                  : undefined,
          })
        }
      >
        Merge into…
      </Button>
      <FlagButton level={detail.kind} id={detail.id} name={name} />
    </div>
  );
}

function listText(values: string[]): string {
  return values.length ? values.join(', ') : 'None';
}

function aliasText(aliases: IdentityAlias[]): string {
  return aliases.length
    ? aliases.map(alias => `${alias.alias}${alias.distinctive ? ' (distinctive)' : ''}`).join(', ')
    : 'None';
}

function Facts({ detail }: { detail: IdentityPlaceDetail }) {
  const organisation = detail.tree.organisation;
  return (
    <dl className="mb-6 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-3xl border bg-card p-4 text-sm">
      {detail.kind !== 'organisation' && (
        <Fact label="Organisation">
          <EntityLink kind="organisation" id={organisation.id}>
            {organisation.display_name}
          </EntityLink>
        </Fact>
      )}
      {detail.kind === 'organisation' && (
        <>
          <Fact label="Parent">
            {detail.parent_id ? (
              <EntityLink kind="organisation" id={detail.parent_id}>
                Parent Organisation
              </EntityLink>
            ) : (
              'None'
            )}
          </Fact>
          <Fact label="OMS IDs">{listText(detail.external_ids)}</Fact>
          <Fact label="Email domains">{listText(detail.email_domains)}</Fact>
        </>
      )}
      {detail.kind === 'site' && (
        <>
          <Fact label="Address">{siteAddress(detail)}</Fact>
          <Fact label="Point">{pointText(detail)}</Fact>
          <Fact label="Head office">{detail.head_office ? 'Yes' : 'No'}</Fact>
        </>
      )}
      {detail.kind === 'department' && (
        <>
          <Fact label="Catalog Site">
            <EntityLink kind="site" id={detail.site_id}>
              {detail.tree.sites.find(site => site.id === detail.site_id)?.display_name ??
                'Catalog Site'}
            </EntityLink>
          </Fact>
          <Fact label="Field">{detail.department_key ?? 'None'}</Fact>
          <Fact label="Specialty code">{detail.specialty_code ?? 'None'}</Fact>
        </>
      )}
      {'reference_key' in detail && (
        <Fact label="Reference key">{detail.reference_key ?? 'None'}</Fact>
      )}
      <Fact label="Aliases">{aliasText(detail.aliases)}</Fact>
      <Fact label="Former IDs">{listText(detail.former_ids)}</Fact>
      <Fact label="Name">{detail.name_locked ? 'Set by a reviewer' : 'From the Mentions'}</Fact>
    </dl>
  );
}

function PlaceTree({
  detail,
  onOpen,
}: {
  detail: IdentityPlaceDetail;
  onOpen: (dialog: Dialogs) => void;
}) {
  const { organisation, children, sites } = detail.tree;
  const current = (kind: IdentityLevel, id: string) => detail.kind === kind && detail.id === id;
  return (
    <div className="rounded-3xl border p-4 text-sm">
      <ul aria-label="Organisation tree" className="space-y-2">
        <li aria-current={current('organisation', organisation.id) ? 'page' : undefined}>
          <div className="font-semibold">
            <EntityLink kind="organisation" id={organisation.id}>
              {organisation.display_name}
            </EntityLink>
          </div>
          {children.length > 0 && (
            <div className="mt-1 text-xs text-muted-foreground">
              Groups{' '}
              {children.map((child, index) => (
                <span key={child.id}>
                  {index > 0 && ', '}
                  <EntityLink kind="organisation" id={child.id}>
                    {child.display_name}
                  </EntityLink>
                </span>
              ))}
            </div>
          )}
          {sites.length === 0 ? (
            <p className="ml-4 mt-2 text-muted-foreground">No Catalog Sites.</p>
          ) : (
            <ul className="ml-4 mt-2 space-y-3 border-l pl-4">
              {sites.map(site => (
                <SiteNode
                  key={site.id}
                  site={site}
                  current={current('site', site.id)}
                  currentDepartment={detail.kind === 'department' ? detail.id : null}
                  onOpen={onOpen}
                />
              ))}
            </ul>
          )}
        </li>
      </ul>
    </div>
  );
}

function SiteNode({
  site,
  current,
  currentDepartment,
  onOpen,
}: {
  site: IdentityTreeSite;
  current: boolean;
  currentDepartment: string | null;
  onOpen: (dialog: Dialogs) => void;
}) {
  return (
    <li aria-label={site.display_name} aria-current={current ? 'page' : undefined}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className={current ? 'font-semibold' : 'font-medium'}>
            <EntityLink kind="site" id={site.id}>
              {site.display_name}
            </EntityLink>
            {site.head_office && (
              <Badge variant="outline" className="ml-2">
                Head office
              </Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground">{siteAddress(site)}</div>
          <div className="text-xs text-muted-foreground">Point {pointText(site)}</div>
        </div>
        <div className="flex flex-wrap gap-1">
          <Button
            size="xs"
            variant="outline"
            aria-label={`Move ${site.display_name}`}
            onClick={() =>
              onOpen({
                kind: 'move-site',
                id: site.id,
                name: site.display_name,
                organisationId: site.organisation_id,
              })
            }
          >
            Move
          </Button>
          <Button
            size="xs"
            variant="outline"
            aria-label={`Merge ${site.display_name}`}
            onClick={() =>
              onOpen({
                kind: 'merge',
                level: 'site',
                id: site.id,
                name: site.display_name,
                filter: { organisation_id: site.organisation_id },
              })
            }
          >
            Merge
          </Button>
          <FlagButton level="site" id={site.id} name={site.display_name} size="xs" />
        </div>
      </div>
      {site.departments.length > 0 && (
        <ul className="ml-4 mt-1 space-y-1 border-l pl-4">
          {site.departments.map((department: IdentityDepartment) => (
            <li
              key={department.id}
              aria-current={currentDepartment === department.id ? 'page' : undefined}
            >
              <EntityLink kind="department" id={department.id}>
                {department.display_name}
              </EntityLink>
              {department.specialty_code && (
                <span className="ml-2 text-xs text-muted-foreground">
                  specialty {department.specialty_code}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function MentionsTable({ detail }: { detail: IdentityPlaceDetail }) {
  if (detail.mentions.length === 0)
    return <p className="text-sm text-muted-foreground">No Mentions link here.</p>;
  const deeper = detail.kind === 'organisation' ? 'site' : null;
  return (
    <div className="overflow-hidden rounded-3xl border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>As the registry wrote it</TableHead>
            <TableHead>Investigator</TableHead>
            <TableHead>Study</TableHead>
            <TableHead>Linked by</TableHead>
            {deeper && <TableHead>Catalog Site</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {detail.mentions.map(mention => {
            const site = mention.links.site;
            return (
              <TableRow key={mention.id}>
                <TableCell className="min-w-[16rem]">
                  <MentionPlace mention={mention} />
                  {!mention.active && (
                    <Badge variant="outline" className="mt-1">
                      No longer in the registry
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {mention.links.person.id ? (
                    <EntityLink kind="person" id={mention.links.person.id}>
                      {mention.person_name ?? 'Unnamed'}
                    </EntityLink>
                  ) : (
                    (mention.person_name ?? <span className="text-muted-foreground">None</span>)
                  )}
                  {mention.email && (
                    <div className="text-xs text-muted-foreground">{mention.email}</div>
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap">{mention.registry_identifier}</TableCell>
                <TableCell>
                  <LinkEvidence link={mention.links[detail.kind]} />
                </TableCell>
                {deeper && (
                  <TableCell>
                    {site.id ? (
                      <>
                        <EntityLink kind="site" id={site.id}>
                          {detail.tree.sites.find(item => item.id === site.id)?.display_name ??
                            'Catalog Site'}
                        </EntityLink>
                        <div>
                          <LinkEvidence link={site} />
                        </div>
                      </>
                    ) : (
                      <span className="text-muted-foreground">Organisation only</span>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

/** Rename, and for Organisations and Catalog Sites add or remove aliases. */
function EditNameDialog({
  detail,
  open,
  onClose,
}: {
  detail: IdentityPlaceDetail;
  open: boolean;
  onClose: () => void;
}) {
  const edit = useEditIdentity();
  const withAliases = detail.kind !== 'department';
  const [name, setName] = useState(detail.display_name);
  const [added, setAdded] = useState('');
  const [removed, setRemoved] = useState<string[]>([]);
  const [distinctive, setDistinctive] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const newAliases = added
    .split('\n')
    .map(alias => alias.trim())
    .filter(Boolean);
  const renamed = name.trim() !== detail.display_name && name.trim() !== '';
  const changed = renamed || newAliases.length > 0 || removed.length > 0;

  const save = async () => {
    setError(null);
    try {
      await edit.mutateAsync({
        level: detail.kind,
        id: detail.id,
        displayName: renamed ? name.trim() : undefined,
        addAliases: newAliases,
        removeAliases: removed,
        distinctive,
        reason,
      });
      toast.success('Saved');
      onClose();
    } catch (failure) {
      setError(identityErrorText(failure, 'Could not save'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={next => !next && !edit.isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{withAliases ? 'Rename and aliases' : 'Rename'}</DialogTitle>
          <DialogDescription>
            A name you set here wins over the spellings in the registry.
          </DialogDescription>
        </DialogHeader>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Name
          <Input aria-label="Name" value={name} onChange={event => setName(event.target.value)} />
        </label>
        {withAliases && (
          <>
            {detail.aliases.length > 0 && (
              <fieldset className="space-y-1 text-sm">
                <legend className="text-xs text-muted-foreground">Remove aliases</legend>
                {detail.aliases.map(alias => (
                  <label key={alias.alias} className="flex items-center gap-2">
                    <Checkbox
                      aria-label={`Remove ${alias.alias}`}
                      checked={removed.includes(alias.alias)}
                      onCheckedChange={checked =>
                        setRemoved(previous =>
                          checked
                            ? [...previous, alias.alias]
                            : previous.filter(item => item !== alias.alias)
                        )
                      }
                    />
                    {alias.alias}
                  </label>
                ))}
              </fieldset>
            )}
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              Add aliases, one per line
              <Textarea
                aria-label="Add aliases"
                rows={3}
                value={added}
                onChange={event => setAdded(event.target.value)}
              />
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                aria-label="Distinctive"
                checked={distinctive}
                onCheckedChange={checked => setDistinctive(checked === true)}
              />
              The new aliases name the Organisation on their own, such as a campus name
            </label>
          </>
        )}
        <ReasonField value={reason} onChange={setReason} />
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={edit.isPending}>
            Cancel
          </Button>
          <Button disabled={!changed || edit.isPending} onClick={() => void save()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
